/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Accordion, AccordionGroup, AccordionHeader, AccordionPanel, Typography2 } from '@coursera/cds-core';

export type AccordionListItem = {
  id: string;
  label: string;
  supportText?: string;
  content: React.ReactNode;
};

export type AccordionListSection = {
  sectionTitle?: string;
  items: AccordionListItem[];
};

export type AccordionListModuleProps = {
  heading: string;
  description?: string;
  sections: AccordionListSection[];
};

const styles = {
  description: css`
    margin-top: var(--cds-spacing-50);
    margin-bottom: var(--cds-spacing-300);
  `,
  section: css`
    margin-bottom: var(--cds-spacing-300);

    &:last-of-type {
      margin-bottom: 0;
    }
  `,
  sectionTitle: css`
    margin-bottom: var(--cds-spacing-150);
  `,
};

const AccordionListModule = ({ heading, description, sections }: AccordionListModuleProps) => (
  <div>
    <Typography2 variant="titleXSmall" component="h3">
      {heading}
    </Typography2>
    {description && (
      <Typography2 variant="bodySecondary" component="p" css={styles.description}>
        {description}
      </Typography2>
    )}
    {sections.map((section, index) => (
      <div css={styles.section} key={section.sectionTitle ?? index}>
        {section.sectionTitle && (
          <Typography2 variant="subtitleMedium" component="h4" css={styles.sectionTitle}>
            {section.sectionTitle}
          </Typography2>
        )}
        <AccordionGroup spacing={8}>
          {section.items.map((item) => (
            <Accordion variant="standard" key={item.id}>
              <AccordionHeader label={item.label} supportText={item.supportText} />
              <AccordionPanel>{item.content}</AccordionPanel>
            </Accordion>
          ))}
        </AccordionGroup>
      </div>
    ))}
  </div>
);

export default AccordionListModule;
